import React, {useEffect, useMemo, useRef, useState} from "react"
import {
    Typography,
    Container,
    Box,
} from "@mui/material";
import NavigationBarView from "../../navbar/components/NavigationBarView";
import LanguageSelectionGridView from "../components/LanguageSelectionGridView";
import DeckSelectionGridView from "../components/DeckSelectionGridView";
import DeckPreviewView from "../components/DeckPreviewView";
import DeckSettingsFormView from "../components/DeckSettingsFormView";
import DeckFieldAssociationView from "../components/DeckFieldAssociationView";
import {DeckCreationStepperView} from "../components/DeckCreationStepperView";
import {useNavigate} from "react-router-dom";
import {BarLoader} from "react-spinners";
import {useTheme} from "@mui/material/styles";
import {useApiRequest} from "../../../common/hooks/useApiRequest";

function DeckImporter() {

    const navigate = useNavigate();
    const theme = useTheme();

    const [dataLoaded, setDataLoaded] = useState(false);

    const [currentStep, setCurrentStep] = useState(0);

    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [selectedDeck, setSelectedDeck] = useState(null);

    const [isCustomDeck, setIsCustomDeck] = useState(false);
    const [customDeckName, setCustomDeckName] = useState('');
    const [customDeckWords, setCustomDeckWords] = useState([]);
    const [customDeckFieldMapping, setCustomDeckFieldMapping] = useState(null);

    const [isDeckConfirmed, setIsDeckConfirmed] = useState(false);

    const [requestStatus, setRequestStatus] = useState("none");

    const languageInfo = useRef([]);

    const {getRequest, postRequest, sendRequest} = useApiRequest();

    const stepTitles = useMemo(() => [
        "Select language",
        "Select word list",
        ...(isCustomDeck ? ["Select fields"] : []),
        "Preview words",
        "Language settings"
    ], [isCustomDeck]);

    useEffect(() => {
        setCurrentStep(
            !isCustomDeck ? (
                    selectedLanguage === null ? 0 :
                        selectedDeck === null ? 1 :
                            !isDeckConfirmed ? 2 : 3
                ) : (customDeckFieldMapping === null ? 2 :
                    !isDeckConfirmed ? 3 : 4
            )
        )
    }, [isCustomDeck, selectedLanguage, selectedDeck, isDeckConfirmed, customDeckFieldMapping]);

    const languageFinder = () => languageInfo.current.find(langDict => langDict.language === selectedLanguage);

    const deckFinder = () => languageInfo.current.find(langDict => langDict.language === selectedLanguage)?.decks
        ?.find(deckDict => deckDict.name === selectedDeck);

    useEffect(() => {
        getRequest('decks/builtin-decks',)
            .then(data => {
                languageInfo.current = data;
                setDataLoaded(true);
            });
    }, []);

    function onBuiltinDeckSelected(deck) {
        setSelectedDeck(deck);
        setIsCustomDeck(false);
    }

    function onCustomAnkiDeckUploaded(file) {

        return new Promise((accept, reject) => {

            if (!file.name.endsWith(".apkg")) {
                reject("not an .apkg file");
                return;
            }

            sendRequest({
                url: '/decks/upload',
                method: "POST",
                objectToSend: {userId: 1},
                fileToSend: file,
                fileDescriptor: "anki-deck",
            }).then(result => {
                accept();
                setIsCustomDeck(true);
                setCustomDeckName(file.name.substring(0, file.name.lastIndexOf('.')))
                setCustomDeckWords(JSON.parse(result.words));
            }).catch(reject);

        });
    }

    function importBuiltinDeck(deckName, reviewMode, words, autoGenerateAudio) {
        setRequestStatus("loading");
        postRequest(`decks/import-builtin-deck`, {
            userId: 1,
            reviewMode: reviewMode,
            wordIds: words.map(word => word["deck_index"]),
            deckName: deckName,
            builtinDeckName: selectedDeck,
            language: selectedLanguage
        }).then(() => {
            setRequestStatus("success");
            window.alert("yay");
        }).catch(() => setRequestStatus("error"));
    }

    function saveCustomDeck(deckName, reviewMode, words, autoGenerateAudio) {
        const mappingAsObj = Object.fromEntries(customDeckFieldMapping.map(([name, displayName, index]) => [name, index]));
        setRequestStatus("loading");
        postRequest('decks/save', {
            userId: 1,
            reviewMode: reviewMode,
            wordIds: words.map(word => word["tempId"]),
            deckName: deckName,
            language: selectedLanguage,
            frontIndex: mappingAsObj["word"],
            backIndex: mappingAsObj["translation"],
            ...("aux" in mappingAsObj ? {auxIndex: mappingAsObj["aux"]} : {}),
            ...("pos" in mappingAsObj ? {posIndex: mappingAsObj["pos"]} : {}),
            sentenceFrontIndexes: [
                ...("sentence1" in mappingAsObj ? [mappingAsObj["sentence1"]] : []),
                ...("sentence2" in mappingAsObj ? [mappingAsObj["sentence2"]] : []),
                ...("sentence3" in mappingAsObj ? [mappingAsObj["sentence3"]] : []),
            ],
            sentenceBackIndexes: [
                ...("sentence_translation1" in mappingAsObj ? [mappingAsObj["sentence_translation1"]] : []),
                ...("sentence_translation2" in mappingAsObj ? [mappingAsObj["sentence_translation2"]] : []),
                ...("sentence_translation3" in mappingAsObj ? [mappingAsObj["sentence_translation3"]] : []),
            ],
            generateSound: autoGenerateAudio
        }).then(() => {
            setRequestStatus("success");
            navigate("/");
        }).catch(() => {
            setRequestStatus("error");
        });
    }


    return !dataLoaded ? null : <Container disableGutters={true} maxWidth={false}>
        <NavigationBarView displayDeckSelectionDropdown={false} displaySearchBar={false} displayVocabularyLink={false}/>
        <Box sx={{maxWidth: 1000, m: '0 auto', mt: 5}} px={{xs:1, sm:2}}>
            <Typography variant="h4" fontWeight="bold" align="center" sx={{mb: 4}}>Add new language</Typography>
            <DeckCreationStepperView currentStep={currentStep} stepNames={stepTitles}/>
            {selectedDeck === null && !isCustomDeck && <DeckSelectionRoutine/>}
            {selectedDeck !== null && <BuiltinDeckRoutine/>}
            {isCustomDeck && <CustomDeckRoutine/>}
            {requestStatus === "loading" && <LoadingView/>}
            {requestStatus === "error" && <ErrorView/>}
        </Box>
    </Container>

    function DeckSelectionRoutine() {
        return (
            <div>
                {selectedLanguage === null && (
                    <LanguageSelectionGridView
                        languageSelectedCallback={({language}) => {
                            setSelectedLanguage(language);
                        }}
                        languages={languageInfo.current.map(langDict => [langDict.language, langDict["language-info"].code])}
                    />
                )}
                {selectedLanguage !== null && (
                    <DeckSelectionGridView
                        decks={languageFinder()?.decks?.map(deckDict => [deckDict.name, deckDict["deck-info"]["description"], deckDict["deck-info"]["word_count"]]) ?? []}
                        builtinDeckSelectedCallBack={onBuiltinDeckSelected}
                        customDeckUploadedCallback={onCustomAnkiDeckUploaded}
                    />
                )}
            </div>
        )
    }

    function BuiltinDeckRoutine() {
        return (
            <div>
                {!isDeckConfirmed && (
                    <DeckPreviewView
                        deckTitle={selectedDeck}
                        columnTitles={deckFinder()["deck-info"]["fields"].map(({name, displayName}) => displayName)}
                        previewWords={deckFinder()["preview-words"].map(word => deckFinder()["deck-info"]["fields"].map(({name, displayName}) =>
                            name === "sentences" ? word["example_sentences"].map(({sentence, sentence_translation}) => sentence).join("\n") :
                                name === "sentence_translations" ? word["example_sentences"].map(({sentence, sentence_translation}) => sentence_translation).join("\n") :
                                    word[name]))}
                        confirmationCallback={() => setIsDeckConfirmed(true)}
                        cancelCallback={() => setSelectedDeck(null)}
                    />
                )}
                {isDeckConfirmed && (
                    <DeckSettingsFormView
                        isBuiltinDeck={true}
                        language={selectedLanguage}
                        deck={selectedDeck}
                        confirmationCallback={({deckName, reviewMode, words, autoGenerateAudio}) => importBuiltinDeck(deckName, reviewMode, words, autoGenerateAudio)}
                    />
                )}
            </div>
        )
    }

    function CustomDeckRoutine() {
        return (
            <div>
                {customDeckFieldMapping === null && (
                    <DeckFieldAssociationView
                        words={customDeckWords}
                        cancelCallback={() => {
                            setCustomDeckName('');
                            setCustomDeckWords([]);
                        }}
                        confirmationCallback={setCustomDeckFieldMapping}
                    />
                )}
                {customDeckFieldMapping !== null && !isDeckConfirmed && (
                    <DeckPreviewView
                        deckTitle={customDeckName}
                        columnTitles={customDeckFieldMapping.map(([name, displayName, index]) => displayName)}
                        previewWords={customDeckWords.slice(0, 10).map(({parts}) => customDeckFieldMapping.map(([name, displayName, index]) => parts[index]))}
                        confirmationCallback={() => setIsDeckConfirmed(true)}
                    />
                )}
                {isDeckConfirmed && (
                    <DeckSettingsFormView
                        isBuiltinDeck={false}
                        language={selectedLanguage}
                        customDeckName={customDeckName}
                        customDeckWords={customDeckWords.map(({ tempId, parts}) => ({
                            tempId: tempId,
                            ...Object.fromEntries(customDeckFieldMapping.map(([name, displayName, index]) => [name, parts[index]]))})
                        )}
                        confirmationCallback={({deckName, reviewMode, words, autoGenerateAudio}) => saveCustomDeck(deckName, reviewMode, words, autoGenerateAudio)}
                    />
                )}
            </div>
        )
    }

    function LoadingView() {
        return (
            <BarLoader color={theme.palette.info.main} width={'100%'}/>
        )
    }

    function ErrorView() {
        return (
            <Typography variant="h6" color={"red"}>Error :(</Typography>
        )
    }


}

export default DeckImporter;